import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import config from '../../config/config';
import TicketAnswerForm from './TicketAnswerForm';

function TicketDetailsModal({
    show,
    onHide,
    ticket,
    getTicketTypeLabel,
    getTicketStatusLabel,
    getKubuniAnswer,
    token
}) {

    const [showFullScreen, setShowFullScreen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [ticketAnswers, setTicketAnswers] = useState([]);

    // 🔹 Synchroniser les réponses si le ticket change
    useEffect(() => {
        setTicketAnswers(ticket?.answers ?? []);
    }, [ticket]);

    // 🔹 URLs mémorisées
    const getTicketImageUrl = useCallback(() => {
        return `${config.ktickUrl}documents/${ticket.reference}/${ticket.imageFilename}`;
    }, [ticket]);

    const getAnswerImageUrl = useCallback((answer) => {
        return `${config.documentsUrl}answers/${answer.uuid}.${answer.imageExtension}`;
    }, []);

    const isPdfAttachment = useCallback((filename) => {
        return filename?.toLowerCase().endsWith('.pdf');
    }, []);

    const formatDate = useCallback((dateString) => {
        return new Date(dateString).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }, []);

    const handleAnswerPosted = (newAnswer) => {
        setTicketAnswers(prev => [...prev, newAnswer]);
    };

    const openFullScreen = (url) => {
        setSelectedImageUrl(url);
        setShowFullScreen(true);
    };

    const closeFullScreen = () => {
        setShowFullScreen(false);
        setSelectedImageUrl(null);
    };

    if (!ticket) return null;

    return (
        <>
            <Modal
                show={show}
                onHide={onHide}
                size="xl"
                aria-labelledby="ticket-details-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="ticket-details-modal">
                        Demande n°{ticket.id} - {getTicketTypeLabel(ticket.ticketType)}
                        <span style={{
                            marginLeft: '10px',
                            padding: '5px 15px',
                            borderRadius: '12px',
                            backgroundColor: '#343a40',
                            color: 'white',
                            fontSize: '14px'
                        }}>
                            {getTicketStatusLabel(ticket.status)}
                        </span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>

                    {/* Description */}
                    <h5>Descriptif</h5>
                    <p>{ticket.description}</p>

                    {ticket.hasImage && ticket.imageFilename && (
                        <div style={{ marginBottom: '20px' }}>
                            {!isPdfAttachment(ticket.imageFilename) && (
                                <img
                                    src={getTicketImageUrl()}
                                    alt="Pièce jointe"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'cover',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => openFullScreen(getTicketImageUrl())}
                                />
                            )}

                            <div>
                                <Button
                                    onClick={() => {
                                        if (isPdfAttachment(ticket.imageFilename)) {
                                            window.open(getTicketImageUrl(), '_blank');
                                        } else {
                                            openFullScreen(getTicketImageUrl());
                                        }
                                    }}
                                >
                                    Voir pièce jointe
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Message Kubuni */}
                    <h5>Message Kubuni</h5>
                    <p>{getKubuniAnswer(ticket)}</p>

                    {/* Réponses */}
                    {ticketAnswers.length > 0 && (
                        <>
                            <h5>Réponses</h5>

                            {ticketAnswers.map((answer, index) => (
                                <div key={answer.id ?? index} style={{
                                    borderLeft: '3px solid #007bff',
                                    paddingLeft: '10px',
                                    marginBottom: '15px'
                                }}>
                                    <p>
                                        <strong>
                                            {answer.createdByLabel === 'KUM'
                                                ? 'Kubuni'
                                                : ticket.website
                                            }
                                            {' ('}
                                            {formatDate(answer.createdAt)}
                                            {') :'}
                                        </strong>{' '}
                                        {answer.content}
                                    </p>

                                    {answer.hasImage && (
                                        <div>
                                            <img
                                                src={getAnswerImageUrl(answer)}
                                                alt="Réponse"
                                                style={{
                                                    width: '80px',
                                                    height: '80px',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() =>
                                                    openFullScreen(getAnswerImageUrl(answer))
                                                }
                                            />
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() =>
                                                    openFullScreen(getAnswerImageUrl(answer))
                                                }
                                            >
                                                Voir l'image
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </>
                    )}

                    {/* Formulaire réponse */}
                    <TicketAnswerForm
                        ticketId={ticket.id}
                        apiUrl={config.apiUrl}
                        token={token}
                        onAnswerPosted={handleAnswerPosted}
                    />

                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <Button onClick={onHide}>
                            Fermer
                        </Button>
                    </div>

                </Modal.Body>
            </Modal>

            {/* Fullscreen viewer */}
            {showFullScreen && selectedImageUrl && (
                <div
                    onClick={closeFullScreen}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 100000
                    }}
                >
                    <img
                        src={selectedImageUrl}
                        alt="Fullscreen"
                        style={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            objectFit: 'contain'
                        }}
                    />
                </div>
            )}
        </>
    );
}

export default TicketDetailsModal;